import * as t from "io-ts";
import nullable from "./nullable";
import { positiveIntegerT } from "./cargo-equipment";
import { withFallback } from "io-ts-types";
export const rentalPricingModelValues = [
    "DAILY",
    "WEEKLY",
    "BIWEEKLY",
    "MONTHLY",
];
export const rentalPricingModelT = t.union([
    t.literal("DAILY"),
    t.literal("WEEKLY"),
    t.literal("BIWEEKLY"),
    t.literal("MONTHLY"),
]);
export const rentalStatusValues = [
    "ACTIVE",
    "COMPLETED",
    "CANCELLED",
    "ON_HOLD",
];
export const rentalStatusT = t.union([
    t.literal("ACTIVE"),
    t.literal("COMPLETED"),
    t.literal("CANCELLED"),
    t.literal("ON_HOLD"),
]);
export const rentalInvoiceStatusValues = ["PENDING", "INVOICED"];
export const rentalInvoiceStatusT = t.union([
    t.literal("PENDING"),
    t.literal("INVOICED"),
]);
export const rentalLineFieldValues = [
    "equipmentType",
    "equipmentNumber",
    "rentalNumber",
    "quantity",
    "unit",
    "chargeableUnits",
    "pricingModel",
    "period",
    "periodStart",
    "periodEnd",
    "calendarDays",
    "clientName",
    "deliveryAddress",
];
export const rentalLineFieldT = t.union([
    t.literal("equipmentType"),
    t.literal("equipmentNumber"),
    t.literal("rentalNumber"),
    t.literal("quantity"),
    t.literal("unit"),
    t.literal("chargeableUnits"),
    t.literal("pricingModel"),
    t.literal("period"),
    t.literal("periodStart"),
    t.literal("periodEnd"),
    t.literal("calendarDays"),
    t.literal("clientName"),
    t.literal("deliveryAddress"),
]);
export const rentalInvoiceTemplateDataT = t.strict({
    lineFields: t.array(rentalLineFieldT),
    separator: t.string,
});
export const createRentalTypes = ({ decimal, date = t.string, }) => {
    const rentalEquipmentInputT = t.strict({
        equipmentId: t.string,
        quantity: positiveIntegerT,
        dailyRate: nullable(decimal),
    });
    const rentalEquipmentT = t.intersection([
        rentalEquipmentInputT,
        t.strict({
            id: t.string,
            deliveredAt: nullable(t.string),
            returnedAt: nullable(t.string),
            equipmentNumber: nullable(t.string),
            equipmentTypeName: nullable(t.string),
            equipmentHomeName: nullable(t.string),
        }),
    ]);
    const baseRentalT = t.strict({
        clientId: nullable(t.string),
        pricingModel: rentalPricingModelT,
        startDate: date,
        expectedEndDate: nullable(date),
        deliveryAddress: nullable(t.string),
        deliveryCity: nullable(t.string),
        deliveryPostcode: nullable(t.string),
        deliveryCountry: nullable(t.string),
        deliveryLat: nullable(t.number),
        deliveryLon: nullable(t.number),
        contactPerson: nullable(t.string),
        contactPhone: nullable(t.string),
        discountPercent: nullable(decimal),
        freeDaysFromSetup: t.number,
        freeWeekends: t.boolean,
        freeHolidays: t.boolean,
        note: nullable(t.string),
    });
    const postRentalT = t.intersection([
        baseRentalT,
        t.strict({ equipment: t.array(rentalEquipmentInputT) }),
    ]);
    const updateRentalT = t.intersection([
        baseRentalT,
        t.strict({
            equipment: t.array(t.strict({ equipmentId: t.string, dailyRate: nullable(decimal) })),
        }),
    ]);
    const rentalT = t.intersection([
        baseRentalT,
        t.strict({
            id: t.string,
            rentalNumber: t.string,
            status: rentalStatusT,
            actualEndDate: nullable(date),
            invoiceStatus: rentalInvoiceStatusT,
            equipment: t.array(rentalEquipmentT),
            sourceCargoId: nullable(t.string),
        }),
    ]);
    const rentalsT = t.array(rentalT);
    const rentalCostLineT = t.strict({
        equipmentId: t.string,
        label: t.string,
        chargeableUnits: t.number,
        unitRate: nullable(decimal),
        quantity: t.number,
        amount: decimal,
    });
    const rentalCostPreviewT = t.strict({
        model: rentalPricingModelT,
        totalCalendarDays: t.number,
        chargeableDays: t.number,
        chargeableWeeks: t.number,
        chargeableBiweekly: t.number,
        chargeableMonths: t.number,
        lines: t.array(rentalCostLineT),
        totalAmount: decimal,
    });
    const rentalBillingItemT = t.strict({
        rentalId: t.string,
        rentalNumber: t.string,
        pricingModel: rentalPricingModelT,
        periodStart: date,
        periodEnd: date,
        chargeableUnits: t.number,
        amount: decimal,
        alreadyInvoiced: t.boolean,
        invoiceable: t.boolean,
        lines: t.array(t.strict({
            equipmentId: t.string,
            label: t.string,
            productId: nullable(t.string),
            unitRate: nullable(decimal),
            chargeableUnits: t.number,
            quantity: t.number,
            amount: decimal,
        })),
    });
    const rentalBillingPreviewT = t.strict({
        clientId: t.string,
        periodStart: date,
        periodEnd: date,
        items: t.array(rentalBillingItemT),
        total: decimal,
    });
    const createRentalInvoiceT = t.strict({
        clientId: t.string,
        periodStart: date,
        periodEnd: date,
        rentalIds: t.array(t.string),
        invoiceRef: nullable(t.string),
        dueDate: nullable(date),
    });
    const rentalHandOverT = t.strict({
        date: withFallback(nullable(date), null),
    });
    return {
        baseRentalT,
        postRentalT,
        rentalHandOverT,
        updateRentalT,
        rentalT,
        rentalsT,
        rentalEquipmentInputT,
        rentalEquipmentT,
        rentalCostLineT,
        rentalCostPreviewT,
        rentalBillingItemT,
        rentalBillingPreviewT,
        createRentalInvoiceT,
    };
};
const wireRentalTypes = createRentalTypes({ decimal: t.string });
export const rentalCostLineT = wireRentalTypes.rentalCostLineT;
export const rentalCostPreviewT = wireRentalTypes.rentalCostPreviewT;
export const rentalBillingItemT = wireRentalTypes.rentalBillingItemT;
export const rentalBillingPreviewT = wireRentalTypes.rentalBillingPreviewT;
export const rentalBillingClientsT = t.array(t.string);
export const rentalHandOverT = wireRentalTypes.rentalHandOverT;
export const createRentalInvoiceT = wireRentalTypes.createRentalInvoiceT;
//# sourceMappingURL=rental.js.map